import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Grid,
  CircularProgress,
  Button,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import PostAddIcon from '@mui/icons-material/PostAdd';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import { useOktaAuth } from '@okta/okta-react';
import { getAllEtos, createEto, deleteEto, updateEto } from '../providers/productFinderAPI';
import { IEto, ICreateEto, IUpdateEto } from '../models/iEto';
import { toast } from 'react-toastify';
import { AddEtoDialog } from '../components/AddEtoDialog';
import { EditEtoDialog } from '../components/EditEtoDialog';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '../providers/queryClient';

const EtosContent = () => {
  const { authState } = useOktaAuth();
  const [etos, setEtos] = useState<IEto[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [etoToEdit, setEtoToEdit] = useState<IEto | null>(null);

  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [rowIdToDelete, setRowIdToDelete] = useState<number | null>(null);

  const fetchEtos = useCallback(async () => {
    setIsLoading(true);
    const token = authState?.accessToken?.accessToken;
    if (!token) {
      setIsLoading(false);
      return;
    }
    try {
      const etoData = await getAllEtos(token);
      setEtos(etoData);
    } catch (error) {
      toast.error('Error fetching ETO records.');
    } finally {
      setIsLoading(false);
    }
  }, [authState]);

  useEffect(() => {
    fetchEtos();
  }, [fetchEtos]);

  const handleEditClick = (eto: IEto) => {
    setEtoToEdit(eto);
    setIsEditDialogOpen(true);
  };

  const handleDeleteClick = (id: number) => {
    setRowIdToDelete(id);
    setIsDeleteConfirmOpen(true);
  };

  const columns: GridColDef[] = [
    { field: 'salesOrgName', headerName: 'Sales Org', flex: 1 },
    { field: 'productGroupName', headerName: 'Product Group', flex: 1 },
    { field: 'productYNumber', headerName: 'Product Y Number', flex: 1 },
    { field: 'productDescription', headerName: 'Description', flex: 2 },
    { field: 'catalog', headerName: 'Catalog', flex: 1 },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      disableColumnMenu: true,
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Tooltip title="Edit ETO">
            <IconButton onClick={() => handleEditClick(params.row)} color="primary">
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete ETO">
            <IconButton onClick={() => handleDeleteClick(params.row.id)} color="error">
              <DeleteForeverIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const handleCreate = async (newEto: ICreateEto) => {
    const token = authState?.accessToken?.accessToken;
    if (!token) {
      toast.error('Authentication error. Please sign in again.');
      return false;
    }
    try {
      await createEto(token, newEto);
      toast.success('New ETO saved successfully!');
      await fetchEtos();
      return true;
    } catch (error) {
      toast.error('Failed to save ETO. Please try again.');
      return false;
    }
  };

  const handleUpdate = async (etoId: number, data: IUpdateEto) => {
    const token = authState?.accessToken?.accessToken;
    if (!token) {
      toast.error('Authentication error. Please sign in again.');
      return false;
    }
    try {
      await updateEto(token, etoId, data);
      toast.success('ETO updated successfully!');
      await fetchEtos(); // Refresh data
      return true;
    } catch (error) {
      toast.error('Failed to update ETO. Please try again.');
      return false;
    }
  };

  const handleConfirmDelete = async () => {
    if (rowIdToDelete === null) return;
    const token = authState?.accessToken?.accessToken;
    if (!token) {
      toast.error('Authentication error. Please sign in again.');
      return;
    }

    setIsDeleting(true);
    try {
      await deleteEto(token, rowIdToDelete);
      toast.success('ETO deleted successfully!');
      await fetchEtos();
    } catch (error) {
      toast.error('Failed to delete ETO.');
    } finally {
      setIsDeleting(false);
      setIsDeleteConfirmOpen(false);
      setRowIdToDelete(null);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Typography variant="h4" component="h1">
            Manage ETOs
          </Typography>
        </Grid>
        <Grid
          sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}
          size={{
            xs: 12,
            md: 6
          }}>
          <Button variant="contained" startIcon={<PostAddIcon />} onClick={() => setIsAddDialogOpen(true)}>
            Add ETO Product
          </Button>
        </Grid>
      </Grid>
      <Box sx={{ flexGrow: 1, width: '100%' }}>
        <DataGrid
          rows={etos}
          columns={columns}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[10, 25, 50]}
        />
      </Box>
      <AddEtoDialog open={isAddDialogOpen} onClose={() => setIsAddDialogOpen(false)} onSave={handleCreate} />
      <EditEtoDialog
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        onSave={handleUpdate}
        eto={etoToEdit}
      />
      <Dialog open={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this ETO record? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteConfirmOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" disabled={isDeleting}>
            {isDeleting ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const Etos = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <EtosContent />
    </QueryClientProvider>
  );
};

export default Etos;
